import React, { useEffect, useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { de } from "date-fns/locale";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const pad = (n) => String(n).padStart(2, "0");
export const isoToSwiss = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
};
export const swissToIso = (txt) => {
  const t = String(txt || "").trim();
  if (!t) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  let m = t.match(/^(\d{1,2})[.\/\-\s](\d{1,2})[.\/\-\s](\d{2}|\d{4})$/);
  if (!m) {
    m = t.match(/^(\d{2})(\d{2})(\d{4}|\d{2})$/); // 24062026 / 240626
    if (!m) return null;
  }
  let [, d, mo, y] = m;
  if (y.length === 2) y = String(2000 + Number(y));
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (date.getFullYear() !== Number(y) || date.getMonth() !== Number(mo) - 1 || date.getDate() !== Number(d)) return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
};

function SwissDateBase({ value, onIso, placeholder = "TT.MM.JJJJ", wrapClassName = "", inputClassName = "", testid = "swiss-date", disabled = false, min, max, clearable = true, id, name, required, autoFocus, onKeyDown, onBlur }) {
  const [text, setText] = useState(isoToSwiss(value));
  const [invalid, setInvalid] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(isoToSwiss(value)); setInvalid(false); }, [value]);

  const commit = () => {
    const iso = swissToIso(text);
    if (iso === null) { setInvalid(true); return; }
    setInvalid(false);
    if (iso !== (value || "")) onIso(iso);
    setText(isoToSwiss(iso));
  };
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;

  return (
    <div className={cn("relative inline-flex items-center", wrapClassName)} data-testid={`${testid}-wrap`}>
      <input
        type="text"
        inputMode="numeric"
        id={id}
        name={name}
        required={required}
        autoFocus={autoFocus}
        value={text}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setInvalid(false); }}
        onBlur={(e) => { commit(); onBlur?.(e); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } onKeyDown?.(e); }}
        className={cn(inputClassName, invalid && "border-rose-400 text-rose-700 bg-rose-50")}
        title={invalid ? "Ungültiges Datum – Format TT.MM.JJJJ" : "Datum im Format TT.MM.JJJJ"}
        aria-invalid={invalid}
        data-testid={testid}
      />
      <div className="absolute right-1 flex items-center gap-0.5">
        {clearable && value && !disabled && !required && (
          <button type="button" tabIndex={-1} onClick={() => { onIso(""); setText(""); setInvalid(false); }} className="rounded p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Leeren" data-testid={`${testid}-clear`}>
            <X className="h-3 w-3" />
          </button>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" tabIndex={-1} disabled={disabled} className="rounded p-0.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="Kalender öffnen" data-testid={`${testid}-calendar`}>
              <CalendarDays className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-[300]" align="start" data-testid={`${testid}-popover`}>
            <Calendar
              mode="single"
              locale={de}
              weekStartsOn={1}
              selected={selected}
              defaultMonth={selected}
              fromDate={min ? new Date(`${min}T00:00:00`) : undefined}
              toDate={max ? new Date(`${max}T00:00:00`) : undefined}
              onSelect={(d) => { if (d) onIso(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`); setOpen(false); }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

/** Kompaktes Schweizer Datumsfeld für Filterleisten: value/onChange(iso) im ISO-Format JJJJ-MM-TT. */
export function SwissDateInput({ value, onChange, className = "", inputClassName = "", ...rest }) {
  return (
    <SwissDateBase
      value={value}
      onIso={(iso) => onChange?.(iso)}
      wrapClassName={className}
      inputClassName={cn("border border-slate-200 rounded-md pl-2 pr-12 py-1 text-xs font-mono w-[8.75rem] bg-white focus:outline-none focus:ring-2 focus:ring-slate-300", inputClassName)}
      {...rest}
    />
  );
}

/** Formularfeld im Stil von <Input type="date">: onChange erhält ein Event-ähnliches Objekt ({ target: { value, name } }). */
export function SwissDateField({ value, onChange, className = "", wrapClassName = "", "data-testid": testid = "swiss-date-field", ...rest }) {
  return (
    <SwissDateBase
      value={value}
      onIso={(iso) => onChange?.({ target: { value: iso, name: rest.name, id: rest.id } })}
      wrapClassName={cn("w-full", wrapClassName)}
      inputClassName={cn("flex h-10 w-full rounded-md border border-input bg-background pl-3 pr-14 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className)}
      testid={testid}
      {...rest}
    />
  );
}
